import { useState, useMemo, useEffect } from 'react';

/**
 * useSubmissionsFilter Hook
 * Manages all filter state and logic for submissions
 * 
 * @param {Object} options - Configuration options
 * @returns {Object} Filter state and handlers
 */
const useSubmissionsFilter = ({ 
  teamLead, 
  selectedUser, 
  submissions = [],
  jobs = []
}) => {
  // Active pipeline statuses - shown by default (Juno schema)
  const activePipelineStatuses = [
    'submission',
    'client submission',
    'interview',
    'offer extended',
    'offer accepted',
    'onboarding'
  ];

  // Early-stage job statuses that require recruiter action
  const actionRequiredJobStatuses = ['accepting_candidates', 'interviewing'];

  // State
  const [selectedStatuses, setSelectedStatuses] = useState(new Set(activePipelineStatuses));
  const [selectedClients, setSelectedClients] = useState(new Set());
  const [selectedJobStatuses, setSelectedJobStatuses] = useState(new Set(actionRequiredJobStatuses));
  const [searchTerm, setSearchTerm] = useState('');

  // Get user's assigned clients based on recruiter hierarchy
  // BUSINESS LOGIC: Recruiters inherit their assigned clients from their team lead
  // This ensures proper data isolation and access control
  const userAssignedClients = useMemo(() => {
    if (!selectedUser) return [];
    
    // RECRUITER INHERITANCE: If user is a recruiter and we have team lead data, 
    // use team lead's assigned clients (standard business practice)
    if (selectedUser.role === 'recruiter' && teamLead) {
      return teamLead.assigned_clients || [];
    }
    
    // DIRECT ASSIGNMENT: For leads/supervisors/managers, use their own assigned clients
    return selectedUser.assigned_clients || [];
  }, [selectedUser, teamLead]);

  // Auto-select user's inherited clients as default filter
  // This provides immediate context-aware filtering based on user's business scope
  useEffect(() => {
    setSelectedClients(new Set(userAssignedClients));
  }, [userAssignedClients]);

  // Get unique clients from submissions AND user's assigned clients
  const uniqueClients = useMemo(() => {
    const clients = new Set();
    
    // Add all assigned clients (from team lead inheritance)
    userAssignedClients.forEach(client => {
      if (client && client.trim()) {
        clients.add(client);
      }
    });
    
    // Add clients from actual submissions (in case there are additional ones)
    submissions.forEach(sub => {
      const job = jobs.find(j => j.id === sub.job_id);
      const client = job?.client_company || 'N/A';
      
      if (client && client !== 'N/A') {
        clients.add(client);
      }
    });
    
    return Array.from(clients).sort();
  }, [submissions, jobs, userAssignedClients]);

  // Toggle functions
  const toggleStatus = (status) => {
    const newStatuses = new Set(selectedStatuses);
    if (newStatuses.has(status)) {
      newStatuses.delete(status);
    } else {
      newStatuses.add(status);
    }
    setSelectedStatuses(newStatuses);
  };

  const toggleClient = (client) => {
    const newClients = new Set(selectedClients);
    if (newClients.has(client)) {
      newClients.delete(client);
    } else {
      newClients.add(client);
    }
    setSelectedClients(newClients);
  };

  const toggleJobStatus = (status) => {
    const newStatuses = new Set(selectedJobStatuses);
    if (newStatuses.has(status)) {
      newStatuses.delete(status);
    } else {
      newStatuses.add(status);
    }
    setSelectedJobStatuses(newStatuses);
  };

  // Reset to default active pipeline view
  const resetFilters = () => {
    setSelectedStatuses(new Set(activePipelineStatuses));
    setSelectedClients(new Set(userAssignedClients));
    setSelectedJobStatuses(new Set(actionRequiredJobStatuses));
    setSearchTerm('');
  };

  return {
    // State
    selectedStatuses,
    selectedClients,
    selectedJobStatuses,
    searchTerm,
    uniqueClients,
    activePipelineStatuses,
    
    // Setters
    setSearchTerm,
    
    // Toggle functions
    toggleStatus,
    toggleClient,
    toggleJobStatus,
    
    // Reset
    resetFilters
  };
};

export default useSubmissionsFilter;
